import { useState, useEffect } from "react";
import api from "../../services/api";

const PORCIONES_DEFAULT = [15, 20, 30, 40, 50, 60];

const TIPOS_VENTA = [
  { key: "torta_configurable", label: "Torta (redonda/cuadrada por porciones)" },
  { key: "precio_manual", label: "Torta especial (precio manual editable)" },
  { key: "precio_fijo", label: "Precio fijo" },
  { key: "precio_por_unidad", label: "Precio por unidad" },
  { key: "paquete", label: "Paquete" },
];

const TIPOS_FIJO = ["precio_fijo", "precio_por_unidad", "paquete"];

const formatMiles = (valor) => {
  const limpio = valor.toString().replace(/\D/g, "");
  if (!limpio) return "";
  return parseInt(limpio).toLocaleString("es-CL");
};

const desformatear = (valor) => valor.toString().replace(/\D/g, "");

const formVacioTorta = () => ({
  nombre: "",
  categoria: "torta",
  tipo_venta: "torta_configurable",
  precioFijo: "",
  cantidadPaquete: "",
  precios: PORCIONES_DEFAULT.map((p) => ({ porcion: p, precio_redonda: "", precio_cuadrada: "" })),
});

export default function Menu() {
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [editando, setEditando] = useState(null);

  const [form, setForm] = useState(formVacioTorta());

  useEffect(() => {
    cargarProductos();
  }, []);

  const cargarProductos = async () => {
    setLoading(true);
    try {
      const res = await api.get("/productos/todos");
      setProductos(res.data);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const abrirNuevo = () => {
    setEditando(null);
    setForm(formVacioTorta());
    setModalAbierto(true);
  };

  const abrirEditar = (producto) => {
    setEditando(producto);
    const tipoVenta = producto.tipo_venta || "torta_configurable";

    if (TIPOS_FIJO.includes(tipoVenta)) {
      const precioActual = producto.precios?.[0];
      setForm({
        nombre: producto.nombre,
        categoria: producto.categoria,
        tipo_venta: tipoVenta,
        precioFijo: precioActual ? formatMiles(precioActual.precio ?? "") : "",
        cantidadPaquete: precioActual?.cantidad_paquete ? String(precioActual.cantidad_paquete) : "",
        precios: [],
      });
    } else {
      const preciosExistentes = PORCIONES_DEFAULT.map((p) => {
        const encontrado = producto.precios?.find((pr) => pr.porcion === p);
        return {
          porcion: p,
          precio_redonda: encontrado ? formatMiles(encontrado.precio_redonda) : "",
          precio_cuadrada: encontrado ? formatMiles(encontrado.precio_cuadrada) : "",
        };
      });
      setForm({
        nombre: producto.nombre,
        categoria: producto.categoria,
        tipo_venta: tipoVenta,
        precioFijo: "",
        cantidadPaquete: "",
        precios: preciosExistentes,
      });
    }
    setModalAbierto(true);
  };

  const handlePrecioChange = (porcion, campo, valor) => {
    const formateado = formatMiles(valor);
    setForm({
      ...form,
      precios: form.precios.map((p) =>
        p.porcion === porcion ? { ...p, [campo]: formateado } : p
      ),
    });
  };

  const handleGuardar = async () => {
    if (!form.nombre) {
      alert("Completa el nombre del producto");
      return;
    }

    let payload;

    if (TIPOS_FIJO.includes(form.tipo_venta)) {
      const precioNum = desformatear(form.precioFijo);
      if (!precioNum) {
        alert("Completa el precio");
        return;
      }
      payload = {
        nombre: form.nombre,
        categoria: form.categoria,
        tipo_venta: form.tipo_venta,
        precios: [
          {
            porcion: 1,
            precio: precioNum,
            cantidad_paquete: form.tipo_venta === "paquete" ? (desformatear(form.cantidadPaquete) || null) : null,
            tamano_nombre: "Unidad",
          },
        ],
      };
    } else {
      const preciosValidos = form.precios
        .filter((p) => p.precio_redonda !== "" || p.precio_cuadrada !== "")
        .map((p) => ({
          porcion: p.porcion,
          precio_redonda: desformatear(p.precio_redonda),
          precio_cuadrada: desformatear(p.precio_cuadrada),
        }));

      if (preciosValidos.length === 0) {
        alert("Completa al menos un precio");
        return;
      }

      payload = {
        nombre: form.nombre,
        categoria: form.categoria,
        tipo_venta: form.tipo_venta,
        precios: preciosValidos,
      };
    }

    try {
      if (editando) {
        await api.put(`/productos/${editando.id}`, payload);
      } else {
        await api.post("/productos", payload);
      }
      setModalAbierto(false);
      cargarProductos();
    } catch (err) {
      console.error(err);
      alert("Error al guardar el producto");
    }
  };

  const handleToggle = async (id) => {
    try {
      await api.patch(`/productos/${id}/toggle`);
      cargarProductos();
    } catch (err) {
      console.error(err);
    }
  };

  const handleEliminar = async (id) => {
    if (!window.confirm("¿Eliminar este producto? Esta acción no se puede deshacer.")) return;
    try {
      await api.delete(`/productos/${id}`);
      cargarProductos();
    } catch (err) {
      console.error(err);
    }
  };

  const resumenPrecios = (producto) => {
    if (!producto.precios?.length) return "Sin precios";
    if (TIPOS_FIJO.includes(producto.tipo_venta)) {
      const p = producto.precios[0];
      if (p.precio == null) return "Sin precio";
      const paquete = p.cantidad_paquete ? ` (paquete de ${p.cantidad_paquete})` : "";
      return `$${parseInt(p.precio).toLocaleString("es-CL")}${paquete}`;
    }
    return producto.precios
      .map((pr) => {
        const partes = [];
        if (pr.precio_redonda) partes.push(`R: $${parseInt(pr.precio_redonda).toLocaleString("es-CL")}`);
        if (pr.precio_cuadrada) partes.push(`C: $${parseInt(pr.precio_cuadrada).toLocaleString("es-CL")}`);
        return `${pr.porcion}p: ${partes.join(" / ")}`;
      })
      .join(" · ");
  };

  const productosPorCategoria = productos.reduce((acc, p) => {
    if (!acc[p.categoria]) acc[p.categoria] = [];
    acc[p.categoria].push(p);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F1F4F9] flex items-center justify-center">
        <div className="text-[#1A56DB] text-sm">Cargando menú...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F1F4F9] p-4">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-lg font-semibold text-gray-900">Menú</h1>
        <button
          onClick={abrirNuevo}
          className="bg-[#1A56DB] hover:bg-[#1E429F] text-white text-sm font-medium px-4 py-2 rounded-lg transition-all"
        >
          + Agregar producto
        </button>
      </div>

      {Object.keys(productosPorCategoria).length === 0 && (
        <div className="text-center text-gray-400 py-16 bg-white border border-[#E5E9F0] rounded-xl">
          No hay productos cargados todavía. Haz click en "+ Agregar producto" para crear el primero.
        </div>
      )}

      {Object.keys(productosPorCategoria).map((categoria) => (
        <div key={categoria} className="mb-4 bg-white border border-[#E5E9F0] rounded-xl overflow-hidden shadow-sm">
          <div className="bg-[#1A56DB] px-4 py-2">
            <span className="text-white text-xs font-semibold uppercase tracking-wider">
              {categoria} ({productosPorCategoria[categoria].length})
            </span>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E5E9F0] text-gray-400 text-xs uppercase">
                <th className="text-left px-4 py-2">Nombre</th>
                <th className="text-left px-4 py-2">Precios</th>
                <th className="text-left px-4 py-2">Estado</th>
                <th className="text-right px-4 py-2">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {productosPorCategoria[categoria].map((p) => (
                <tr key={p.id} className="border-b border-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">{p.nombre}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{resumenPrecios(p)}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${p.activo ? "bg-green-50 text-green-700 border border-green-200" : "bg-gray-100 text-gray-500 border border-[#E5E9F0]"}`}>
                      {p.activo ? "Activo" : "Inactivo"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right space-x-3">
                    <button onClick={() => abrirEditar(p)} className="text-[#1A56DB] text-xs font-medium hover:underline">Editar</button>
                    <button onClick={() => handleToggle(p.id)} className="text-amber-600 text-xs font-medium hover:underline">{p.activo ? "Desactivar" : "Activar"}</button>
                    <button onClick={() => handleEliminar(p.id)} className="text-red-600 text-xs font-medium hover:underline">Eliminar</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {modalAbierto && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center backdrop-blur-sm p-4">
          <div className="bg-white border border-[#E5E9F0] rounded-2xl p-6 w-full max-w-2xl shadow-xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-gray-900 font-semibold text-lg mb-1">
              {editando ? "Editar producto" : "Agregar producto"}
            </h3>
            <p className="text-gray-400 text-xs mb-4">
              Completa el nombre, tipo de venta y precios de este producto
            </p>

            <div className="mb-3">
              <label className="text-xs text-gray-500 uppercase tracking-wider font-medium">
                Nombre del producto
              </label>
              <input
                type="text"
                value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                placeholder="Ej: Torta Selva Negra"
                className="w-full mt-1 bg-[#F1F4F9] border border-[#E5E9F0] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#1A56DB] focus:ring-1 focus:ring-[#1A56DB]"
              />
            </div>

            <div className="mb-3">
              <label className="text-xs text-gray-500 uppercase tracking-wider font-medium">
                Categoría
              </label>
              <select
                value={form.categoria}
                onChange={(e) => setForm({ ...form, categoria: e.target.value })}
                className="w-full mt-1 bg-[#F1F4F9] border border-[#E5E9F0] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#1A56DB] focus:ring-1 focus:ring-[#1A56DB]"
              >
                <option value="torta">Torta</option>
                <option value="cheesecake">Cheesecake</option>
                <option value="tarta">Tarta</option>
                <option value="queque">Queque</option>
                <option value="coctel">Cóctel</option>
                <option value="mousse">Mousse</option>
                <option value="pie">Pie</option>
                <option value="pan">Pan</option>
                <option value="otro">Otro</option>
              </select>
            </div>

            <div className="mb-4">
              <label className="text-xs text-gray-500 uppercase tracking-wider font-medium">
                Tipo de venta
              </label>
              <select
                value={form.tipo_venta}
                onChange={(e) => setForm({ ...form, tipo_venta: e.target.value })}
                className="w-full mt-1 bg-[#F1F4F9] border border-[#E5E9F0] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#1A56DB] focus:ring-1 focus:ring-[#1A56DB]"
              >
                {TIPOS_VENTA.map((t) => (
                  <option key={t.key} value={t.key}>{t.label}</option>
                ))}
              </select>
              <p className="text-xs text-gray-400 mt-1">
                Nota: los tipos distintos a "Torta" solo quedar&aacute;n operativos una vez aplicadas las migraciones pendientes.
              </p>
            </div>

            {TIPOS_FIJO.includes(form.tipo_venta) ? (
              <div className="space-y-3 mb-4">
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wider font-medium">Precio</label>
                  <div className="relative mt-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={form.precioFijo}
                      onChange={(e) => setForm({ ...form, precioFijo: formatMiles(e.target.value) })}
                      placeholder="0"
                      className="w-full bg-[#F1F4F9] border border-[#E5E9F0] rounded-lg pl-7 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#1A56DB] focus:ring-1 focus:ring-[#1A56DB]"
                    />
                  </div>
                </div>
                {form.tipo_venta === "paquete" && (
                  <div>
                    <label className="text-xs text-gray-500 uppercase tracking-wider font-medium">Unidades por paquete</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={form.cantidadPaquete}
                      onChange={(e) => setForm({ ...form, cantidadPaquete: formatMiles(e.target.value) })}
                      placeholder="Ej: 100"
                      className="w-full mt-1 bg-[#F1F4F9] border border-[#E5E9F0] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#1A56DB] focus:ring-1 focus:ring-[#1A56DB]"
                    />
                  </div>
                )}
                <p className="text-xs text-gray-400">
                  Este producto no usa formato redonda/cuadrada ni porciones. Las variedades/opciones se gestionan aparte.
                </p>
              </div>
            ) : (
              <>
                <div className="mb-2">
                  <label className="text-xs text-gray-500 uppercase tracking-wider font-medium">
                    Precio según cantidad de porciones
                  </label>
                  <p className="text-xs text-gray-400 mt-0.5 mb-3">
                    Escribe el precio para formato redondo y/o cuadrado en cada tamaño. Deja en blanco los que no ofrezcas.
                  </p>
                </div>

                <div className="space-y-3 mb-4">
                  {form.precios.map((p) => (
                    <div key={p.porcion} className="flex items-center gap-3">
                      <div className="w-24 flex-shrink-0">
                        <span className="text-sm font-medium text-gray-700">
                          {p.porcion} porciones
                        </span>
                      </div>
                      <div className="flex-1 relative">
                        <label className="text-[10px] text-gray-400 uppercase tracking-wider block mb-0.5">Redonda</label>
                        <span className="absolute left-3 top-7 -translate-y-1/2 text-gray-400 text-sm">$</span>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={p.precio_redonda}
                          onChange={(e) => handlePrecioChange(p.porcion, "precio_redonda", e.target.value)}
                          placeholder="0"
                          className="w-full bg-[#F1F4F9] border border-[#E5E9F0] rounded-lg pl-7 pr-3 py-2 text-sm focus:outline-none focus:border-[#1A56DB] focus:ring-1 focus:ring-[#1A56DB]"
                        />
                      </div>
                      <div className="flex-1 relative">
                        <label className="text-[10px] text-gray-400 uppercase tracking-wider block mb-0.5">Cuadrada</label>
                        <span className="absolute left-3 top-7 -translate-y-1/2 text-gray-400 text-sm">$</span>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={p.precio_cuadrada}
                          onChange={(e) => handlePrecioChange(p.porcion, "precio_cuadrada", e.target.value)}
                          placeholder="0"
                          className="w-full bg-[#F1F4F9] border border-[#E5E9F0] rounded-lg pl-7 pr-3 py-2 text-sm focus:outline-none focus:border-[#1A56DB] focus:ring-1 focus:ring-[#1A56DB]"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            <div className="flex gap-2">
              <button
                onClick={handleGuardar}
                className="flex-1 bg-[#1A56DB] hover:bg-[#1E429F] text-white font-semibold py-2.5 rounded-lg transition-all"
              >
                {editando ? "Guardar cambios" : "Crear producto"}
              </button>
              <button
                onClick={() => setModalAbierto(false)}
                className="bg-gray-100 hover:bg-gray-200 text-gray-600 px-4 rounded-lg transition-all"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
